/**
 * useGoogleReviews — fetches the dynamic Google reviews payload from the
 * backend (`GET /api/google/reviews`) and exposes loading/error state.
 *
 * The backend always returns a usable payload (it falls back to a static set
 * of reviews when the API key is missing or the upstream call fails), so the
 * UI can trust `data` to be non-null after `loading` becomes false.
 */
import { useEffect, useState } from "react";
import { logger } from "@/lib/logger";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATIC_FALLBACK = {
  rating: 4.9,
  total: 71,
  place_id: "",
  reviews: [],
  updated_at: "",
  source: "client-fallback",
};

export const useGoogleReviews = () => {
  const [data, setData] = useState(STATIC_FALLBACK);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      try {
        const res = await fetch(`${API_URL}/google/reviews`, {
          headers: { Accept: "application/json" },
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (!cancelled) setData(json);
      } catch (err) {
        if (!cancelled) setError(err);
        logger.error("Failed to fetch Google reviews:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchData();
    return () => {
      cancelled = true;
    };
  }, []);

  return { data, loading, error };
};
